import { callEdgeFunction, supabase } from './supabase';
import type {
  PromoCode,
  PromoCodeCategory,
  PromoCodeStats,
  PromoCodeListResponse,
  PromoCodeDetailResponse,
  PlanOption,
} from '@/types/promoCode';

const FN = 'admin-manage-promo-codes';

// === Promo Codes ===

export async function getPromoCodes(
  page = 1,
  pageSize = 20,
  status?: string,
  categoryId?: string,
  discountType?: string,
  search?: string,
): Promise<PromoCodeListResponse> {
  return callEdgeFunction<PromoCodeListResponse>(FN, {
    action: 'list',
    page,
    pageSize,
    status: status || 'all',
    categoryId: categoryId || undefined,
    discountType: discountType || 'all',
    search: search || undefined,
  });
}

export async function getPromoCodeDetail(promoCodeId: string): Promise<PromoCodeDetailResponse> {
  return callEdgeFunction<PromoCodeDetailResponse>(FN, { action: 'get', promoCodeId });
}

export async function createPromoCode(request: {
  code: string;
  discountType: string;
  percentOff?: number;
  amountOffCents?: number;
  currency?: string;
  duration: string;
  durationInMonths?: number;
  restrictedPlanIds?: string[];
  maxRedemptions?: number;
  expiresAt?: string;
  categoryId?: string;
  internalName?: string;
  notes?: string;
}): Promise<{ success: boolean; promoCode: PromoCode }> {
  return callEdgeFunction(FN, { action: 'create', ...request });
}

export async function bulkCreatePromoCodes(request: {
  prefix: string;
  quantity: number;
  codeFormat: 'sequential' | 'random';
  discountType: string;
  percentOff?: number;
  amountOffCents?: number;
  currency?: string;
  duration: string;
  durationInMonths?: number;
  restrictedPlanIds?: string[];
  maxRedemptions?: number;
  expiresAt?: string;
  categoryId?: string;
  internalName?: string;
  notes?: string;
}): Promise<{ success: boolean; count: number; bulkGroupId: string }> {
  return callEdgeFunction(FN, { action: 'bulk_create', ...request }, { maxRetries: 1 });
}

export async function updatePromoCode(
  promoCodeId: string,
  updates: { categoryId?: string; internalName?: string; notes?: string; maxRedemptions?: string },
): Promise<{ success: boolean; promoCode: PromoCode }> {
  return callEdgeFunction(FN, { action: 'update', promoCodeId, ...updates });
}

export async function deactivatePromoCode(promoCodeId: string): Promise<{ success: boolean }> {
  return callEdgeFunction(FN, { action: 'deactivate', promoCodeId });
}

export async function reactivatePromoCode(promoCodeId: string): Promise<{ success: boolean }> {
  return callEdgeFunction(FN, { action: 'reactivate', promoCodeId });
}

export async function getPromoCodeStats(): Promise<PromoCodeStats> {
  return callEdgeFunction<PromoCodeStats>(FN, { action: 'stats' });
}

// === Categories ===

export async function getPromoCodeCategories(): Promise<PromoCodeCategory[]> {
  return callEdgeFunction<PromoCodeCategory[]>(FN, { action: 'list_categories' });
}

export async function createPromoCodeCategory(
  name: string,
  description?: string,
  color?: string,
): Promise<PromoCodeCategory> {
  return callEdgeFunction<PromoCodeCategory>(FN, { action: 'create_category', name, description, color });
}

// === Plans (for restriction dropdowns) ===

export async function getPlans(): Promise<PlanOption[]> {
  const { data, error } = await supabase
    .from('plans')
    .select('plan_id, name')
    .order('name');

  if (error) throw error;
  return (data || []) as PlanOption[];
}
